/**
 * Domain models for the player website.
 *
 * CamelCase, UI-shaped. The PHP Site API’s snake_case JSON lives in `wire.ts`
 * and is mapped here, so a contract change only ever touches that file plus
 * `endpoints.ts`.
 */

export type Locale = 'zh' | 'en';

/** A pair of localized strings; the API keeps both languages for catalog content. */
export interface LocalizedText {
  zh: string;
  en: string;
}

export interface Paginated<T> {
  items: T[];
  page: number;
  perPage: number;
  total: number;
  totalPages: number;
  hasMore: boolean;
}

export interface ApiMeta {
  page?: number;
  perPage?: number;
  total?: number;
  totalPages?: number;
  hasMore?: boolean;
  self?: unknown;
  hiddenLockedCount?: number;
  unlockCount?: number;
  notice?: string;
  [key: string]: unknown;
}

/* ------------------------------------------------------------------ players */

export interface PlayerRole {
  code: string;
  name: LocalizedText;
  badgeColor: string;
}

/** Public, privacy-safe player summary — everything a leaderboard may show. */
export interface PlayerSummary {
  publicPlayerId: string;
  nickname: string;
  avatarUrl: string | null;
  countryCode: string | null;
  roles: PlayerRole[];
}

export interface PlayerProfile extends PlayerSummary {
  bio: string | null;
  socialLinks: string[];
  birthday: string | null;
  gender: 'male' | 'female' | 'other' | 'prefer_not_to_say' | null;
  joinedAt: string;
  points: number;
  achievementCount: number;
  speedrunCount: number;
  followersCount: number;
  followingCount: number;
  /** Null when the viewer is anonymous. */
  isFollowing: boolean | null;
}

export interface BanState {
  type: 'temporary' | 'permanent';
  scope: 'account' | 'community';
  reason: string | null;
  startsAt: string | null;
  endsAt: string | null;
  isPermanent: boolean;
}

/** The authenticated player. Server-authoritative; never derived client-side. */
export interface Viewer extends PlayerProfile {
  email: string | null;
  username: string;
  locale: Locale;
  status: string;
  emailVerified: boolean;
  profileIncomplete: boolean;
  deviceBound: boolean;
  unreadNotifications: number;
  /** Admin permission codes. Empty for ordinary players. */
  permissions: string[];
  isSuperAdmin: boolean;
  ban: BanState | null;
}

export interface PlayerPoints {
  points: number;
  rank: number | null;
  breakdown: {
    achievement: number;
    speedrun: number;
    adminAdjust: number;
    other: number;
  };
}

export interface MyPoints extends PlayerPoints {
  totalEarned: number;
  totalDeducted: number;
}

/* ------------------------------------------------------------- achievements */

export type AchievementKind = 'normal' | 'special';

export interface NamedRef {
  id: number;
  code: string;
  name: LocalizedText;
}

export interface Achievement {
  id: number;
  code: string;
  game: NamedRef;
  type: NamedRef;
  name: LocalizedText;
  description: LocalizedText;
  nameLocalized: string;
  descriptionLocalized: string;
  iconUrl: string | null;
  kind: AchievementKind;
  /** Hidden until this viewer has unlocked it. Independent of `kind`. */
  isHidden: boolean;
  points: number;
  unlocked: boolean;
  unlockedAt: string | null;
}

export interface AchievementCatalog extends Paginated<Achievement> {
  hiddenLockedCount: number;
}

export interface AchievementDetail {
  achievement: Achievement;
  unlockCount: number;
  recentUnlockers: PlayerSummary[];
}

/* ---------------------------------------------------------------- speedruns */

export interface GameRef {
  id: number;
  code: string;
  name: LocalizedText;
  /** Missing on older payloads — treat as enabled. */
  isEnabled: boolean;
}

export interface GamePlatform {
  id: number;
  code: string;
  name: LocalizedText;
  nameLocalized: string;
}

export interface GameVersion {
  id: number;
  gameId: number;
  code: string;
  name: string;
  releasedAt: string | null;
}

export interface SpeedrunTier {
  id: number;
  name: LocalizedText;
  nameLocalized: string;
  maxTimeMs: number;
  points: number;
}

export interface SpeedrunCategory {
  id: number;
  gameId: number;
  code: string;
  name: LocalizedText;
  nameLocalized: string;
  description: LocalizedText;
  timingMethod: 'rta' | 'igt';
  tiers: SpeedrunTier[];
}

export type SpeedrunStatus = 'pending' | 'approved' | 'rejected' | 'revoked' | 'withdrawn';

export interface SpeedrunPlayer {
  publicPlayerId: string;
  nickname: string;
  avatarUrl: string | null;
  countryCode: string | null;
}

export interface SpeedrunSubmission {
  id: number;
  player: SpeedrunPlayer;
  game: GameRef;
  platform: GamePlatform;
  version: GameVersion;
  category: SpeedrunCategory;
  timeMs: number;
  /** Server-formatted `HH:MM:SS.mmm`. Display this; do not re-author the number. */
  timeFormatted: string;
  playedAt: string | null;
  status: SpeedrunStatus;
  tier: SpeedrunTier | null;
  pointsAwarded: number;
  notes: string | null;
  proofUrl: string | null;
  review: { note: string | null; reviewedAt: string | null } | null;
  createdAt: string;
}

export interface SpeedrunSubmitInput {
  gameId: number;
  platformId: number;
  versionId: number;
  categoryId: number;
  timeMs: number;
  playedAt: string;
  notes?: string;
  proofUrl?: string;
}

export interface SpeedrunOptions {
  games: GameRef[];
  platforms: GamePlatform[];
  versions: GameVersion[];
  categories: SpeedrunCategory[];
}

/* ------------------------------------------------------------------- points */

export type PointsSource = 'achievement' | 'speedrun' | 'admin_adjust' | 'reversal' | 'system';

export interface PointsEntry {
  id: number;
  delta: number;
  balanceAfter: number;
  sourceType: PointsSource;
  sourceId: number | null;
  reason: string;
  reversesEntryId: number | null;
  createdAt: string;
  createdAtUnix: number | null;
}

/* -------------------------------------------------------------- leaderboard */

export interface PointsLeaderboardRow {
  rank: number;
  player: PlayerSummary;
  points: number;
  achievementCount: number;
}

export interface SpeedrunLeaderboardRow {
  rank: number;
  player: PlayerSummary;
  timeMs: number;
  timeFormatted: string;
  platform: GamePlatform;
  version: GameVersion;
  tier: SpeedrunTier | null;
  submissionId: number;
  playedAt: string | null;
}

export interface LeaderboardPage<T> extends Paginated<T> {
  /** The viewer's own row from `meta.self`, even when outside the current page. */
  self: T | null;
}

export interface MyPointsRank {
  rank: number | null;
  totalRanked: number;
  points: number;
}

export interface MySpeedrunRank {
  rank: number | null;
  totalRanked: number;
  timeMs: number | null;
  timeFormatted: string | null;
  submissionId: number | null;
}

/* ---------------------------------------------------------------- community */

export interface CommunityBoard {
  id: number;
  code: string;
  name: LocalizedText;
  nameLocalized: string;
  description: LocalizedText;
}

export type PostStatus = 'published' | 'pending' | 'hidden' | 'removed';

export interface CommunityPost {
  id: number;
  boardId: number;
  board: CommunityBoard | null;
  author: PlayerSummary;
  title: string;
  /** Plain text. The server sanitizes; never render as HTML. */
  body: string;
  status: PostStatus;
  likeCount: number;
  commentCount: number;
  viewCount: number;
  isLikedByMe: boolean;
  isPinned: boolean;
  isLocked: boolean;
  createdAt: string;
  updatedAt: string | null;
}

export interface CommunityComment {
  id: number;
  postId: number;
  parentId: number | null;
  parentAuthor: PlayerSummary | null;
  author: PlayerSummary;
  body: string;
  status: string;
  likeCount: number;
  isLikedByMe: boolean;
  createdAt: string;
  replies: CommunityComment[];
}

export type ReportTargetType = 'post' | 'comment' | 'user';

export type ReportReason = 'spam' | 'abuse' | 'nsfw' | 'cheating' | 'off_topic' | 'other';

export interface ReportInput {
  targetType: ReportTargetType;
  targetId: number;
  reasonCode: ReportReason;
  detail?: string;
}

/* ------------------------------------------------------------ notifications */

export type NotificationType =
  | 'achievement_unlocked'
  | 'achievement_revoked'
  | 'speedrun_approved'
  | 'speedrun_rejected'
  | 'speedrun_revoked'
  | 'points_adjusted'
  | 'account_banned'
  | 'account_unbanned'
  | 'account_updated'
  | 'post_commented'
  | 'post_liked'
  | 'comment_replied'
  | 'new_follower'
  | 'report_resolved'
  | 'admin_message'
  | 'system';

export interface AppNotification {
  id: number;
  type: NotificationType;
  title: string;
  body: string;
  data: Record<string, unknown>;
  link: string | null;
  read: boolean;
  createdAt: string;
}

/* --------------------------------------------------------------- dictionaries */

export interface Country {
  code: string;
  nameZh: string;
  nameEn: string;
}

export interface SiteDictionaries {
  games: GameRef[];
  platforms: GamePlatform[];
  versions: GameVersion[];
  categories: SpeedrunCategory[];
  achievementTypes: NamedRef[];
  boards: CommunityBoard[];
}

/* --------------------------------------------------------------- account */

export interface AuthSession {
  id: string;
  ipMasked: string;
  deviceHint: string;
  lastActiveAt: string;
  current: boolean;
}

export interface GameToken {
  id: number;
  deviceHint: string;
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
}

export interface BoundDevice {
  platform: string;
  nameZh: string;
  nameEn: string;
  model: string;
  uidMasked: string;
  boundAt: string;
  status: string;
}

export interface DeviceBinding {
  bound: boolean;
  device: BoundDevice | null;
  /** Occupied platform slots (windows + android can both be bound). */
  devices: BoundDevice[];
}

/* --------------------------------------------------------------- site state */

export interface FeatureFlags {
  webRegistration: boolean;
  gameRegistration: boolean;
  avatarUpload: boolean;
  community: boolean;
  speedrunSubmission: boolean;
}

export interface DeveloperContactField {
  label: LocalizedText;
  labelLocalized: string;
  value: string;
}

export interface DeveloperContact {
  intro: LocalizedText;
  introLocalized: string;
  fields: DeveloperContactField[];
}

export interface SiteNavLink {
  id: number;
  code: string;
  url: string;
  name: LocalizedText;
  nameLocalized: string;
}

export interface LegalDocument {
  id: number;
  code: string;
  name: LocalizedText;
  nameLocalized: string;
  body: LocalizedText;
  bodyLocalized: string;
}

export interface LegalCopy {
  columnTitle: LocalizedText;
  communityGuidelines: LocalizedText[];
  documents: LegalDocument[];
}

export interface SiteSettings {
  siteName: string;
  siteDescription: string;
  logoUrl: string | null;
  faviconUrl: string | null;
  defaultLocale: Locale;
  availableLocales: Locale[];
  announcement: { enabled: boolean; text: string; textZh: string; textEn: string } | null;
  features: FeatureFlags;
  maintenance: { enabled: boolean; message: string | null; messageZh: string; messageEn: string };
  developerContact: DeveloperContact;
  terms: { zh: string; en: string; localized: string };
  legal: LegalCopy;
  communityRulesUrl: string;
  privacyPolicyUrl: string;
  navLinks: { header: SiteNavLink[]; footer: SiteNavLink[] };
  serverTime: string | null;
  apiVersion: string;
}

/* ------------------------------------------------------------------- errors */

/** Normalized API failure. Every rejected request surfaces one of these. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  /** Field-level messages keyed by form field name. */
  readonly fieldErrors: Record<string, string>;
  readonly retryAfterSeconds: number | null;
  readonly requestId: string | null;
  readonly data: Record<string, unknown> | null;

  constructor(init: {
    status: number;
    code: string;
    message: string;
    fieldErrors?: Record<string, string>;
    retryAfterSeconds?: number | null;
    requestId?: string | null;
    data?: Record<string, unknown> | null;
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.fieldErrors = init.fieldErrors ?? {};
    this.retryAfterSeconds = init.retryAfterSeconds ?? null;
    this.requestId = init.requestId ?? null;
    this.data = init.data ?? null;
  }

  get normalizedCode() {
    return this.code.toUpperCase();
  }

  get isUnauthorized() {
    return this.status === 401;
  }

  get isForbidden() {
    return this.status === 403;
  }

  get isBanned() {
    return this.normalizedCode === 'ACCOUNT_BANNED';
  }

  get isFeatureDisabled() {
    return this.normalizedCode === 'FEATURE_DISABLED';
  }

  get isRateLimited() {
    return this.status === 429 || this.normalizedCode === 'RATE_LIMITED';
  }

  get isMaintenance() {
    return this.normalizedCode === 'MAINTENANCE_MODE';
  }

  get isCsrfMismatch() {
    return this.normalizedCode === 'CSRF_TOKEN_MISMATCH';
  }
}
